import { useEffect, useState } from 'react';
import { useApi } from '../services/api';

interface BingoLettersProps {
    gameId: string;
    size?: 'small' | 'medium' | 'large';
}

/**
 * Displays B-I-N-G-O letters, lighting up one letter per completed bingo line
 * Each letter has a unique animation when lit:
 * - B: rotates left/right
 * - I: bounces up/down
 * - N: flips
 * - G: rotates 360°
 * - O: pulses/blinks
 */
export default function BingoLetters({ gameId, size = 'medium' }: BingoLettersProps) {
    const api = useApi();
    const [bingoCount, setBingoCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadBingoCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    async function loadBingoCount() {
        try {
            setLoading(true);
            const data = await api.getMyGameStats(gameId);
            setBingoCount(Math.min(data.bingoLines || 0, 5)); // Cap at 5
        } catch (error) {
            console.error('Failed to load bingo count:', error);
            setBingoCount(0);
        } finally {
            setLoading(false);
        }
    }

    const letters = ['B', 'I', 'N', 'G', 'O'];

    const sizeClasses = {
        small: 'text-xl w-7 h-7',
        medium: 'text-2xl w-10 h-10',
        large: 'text-3xl w-14 h-14',
    };

    const sizeClass = sizeClasses[size];

    // Unique animation for each letter when lit
    const letterAnimations: Record<string, string> = {
        'B': 'animate-bingo-rotate', // Rotate left-right
        'I': 'animate-bingo-bounce', // Bounce up-down
        'N': 'animate-bingo-flip',   // Flip
        'G': 'animate-bingo-spin',   // Rotate 360
        'O': 'animate-bingo-pulse',  // Pulse/blink
    };

    if (loading) {
        return (
            <div className="flex gap-1">
                {letters.map((letter) => (
                    <div
                        key={letter}
                        className={`${sizeClass} flex items-center justify-center rounded-lg bg-gray-300 animate-pulse`}
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            {/* CSS for animations */}
            <style>{`
                @keyframes bingo-rotate {
                    0%, 100% { transform: rotate(-8deg); }
                    50% { transform: rotate(8deg); }
                }
                @keyframes bingo-bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                @keyframes bingo-flip {
                    0%, 100% { transform: rotateY(0deg); }
                    50% { transform: rotateY(180deg); }
                }
                @keyframes bingo-spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes bingo-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.7; transform: scale(1.1); }
                }
                .animate-bingo-rotate { animation: bingo-rotate 1.5s ease-in-out infinite; }
                .animate-bingo-bounce { animation: bingo-bounce 1s ease-in-out infinite; }
                .animate-bingo-flip { animation: bingo-flip 2s ease-in-out infinite; }
                .animate-bingo-spin { animation: bingo-spin 3s linear infinite; }
                .animate-bingo-pulse { animation: bingo-pulse 1.2s ease-in-out infinite; }
            `}</style>
            <div className="flex gap-1">
                {letters.map((letter, index) => {
                    const isLit = index < bingoCount;
                    return (
                        <div
                            key={letter}
                            className={`
                                ${sizeClass}
                                flex items-center justify-center
                                font-bold rounded-lg
                                transition-all duration-300
                                ${isLit
                                    ? `bg-gradient-to-br from-green-400 to-green-600 text-white shadow-lg ${letterAnimations[letter]}`
                                    : 'bg-gray-300 text-gray-500'
                                }
                            `}
                            title={isLit ? `${index + 1} bingo line${index > 0 ? 's' : ''} completed!` : 'Not completed yet'}
                        >
                            {letter}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
